import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { ApiFactory } from "../../api";
import { SearchResult, SearchResults, SelectedRow } from "../../models/search-result";
import { Filters, SortDirection, Sorting } from "../../models/sorting";
import { ReducerErrorToaster } from "../../utils/ReducerUtils";

interface SearchState {
  data: SearchResults;
  filters: Filters | undefined;
  sorting: Sorting;
  sortingOrder: SortDirection;
  search: string;
  fetching: boolean;
  latestSearchRequestId?: string;
  fetchingBijlage: boolean;
  selectedConcepts: SearchResult[];
}

const initialState: SearchState = {
  fetching: false,
  fetchingBijlage: false,
  data: {
    aantal: 0,
    resultaten: [],
    start: 0,
    totaal: 0,
  },
  filters: "hoofddocument",
  sorting: "laatst-bewerkt",
  sortingOrder: "desc",
  search: "",
  selectedConcepts: [],
};

export const searchDocuments = createAsyncThunk<SearchResults, { pagina: number }, { state: RootState }>(
  "search/get",
  async (args: { pagina: number }, thunkAPI) => {
    const api = ApiFactory.createSearchApi();
    const state: SearchState = thunkAPI.getState().search;

    try {
      return await api.searchOrgDocuments(
        (args.pagina - 1) * state.data.resultaten.length,
        state.sorting,
        state.filters,
        state.sortingOrder,
        state.search
      );
    } catch (error: any) {
      ReducerErrorToaster(thunkAPI, error);
      throw error;
    }
  }
);

export const searchBijlages = createAsyncThunk<SelectedRow, { uuid: string; rowIndex: number }, { state: RootState }>(
  "metadata/getBijlages",
  async ({ uuid, rowIndex }, thunkAPI) => {
    const api = ApiFactory.createDocumentMetadataApi();

    try {
      const results = await api.getDocumentBijlages(uuid);

      return {
        index: rowIndex,
        foldableContent: results,
      };
    } catch (error: any) {
      ReducerErrorToaster(thunkAPI, error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const searchState = createSlice({
  name: "search",
  initialState,
  reducers: {
    clearSearch: (state) => {
      state.data.aantal = 0;
      state.data.resultaten = [];
      state.data.start = 0;
      state.data.totaal = 0;
    },
    updateSorting: (state, action: PayloadAction<{ sortBy: Sorting; sortDirection: SortDirection }>) => {
      state.sorting = action.payload.sortBy;
      state.sortingOrder = action.payload.sortDirection;
    },
    updateSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    updateFilter: (state, action: PayloadAction<Filters | undefined>) => {
      state.filters = action.payload;
    },

    updateSelectedConcepts: (state, action: PayloadAction<SearchResult>) => {
      if (state.selectedConcepts.find((result: SearchResult) => result.document.pid === action.payload.document.pid))
        state.selectedConcepts = state.selectedConcepts.filter(
          (result: SearchResult) => result.document.pid !== action.payload.document.pid
        );
      else state.selectedConcepts.push(action.payload);
    },
    clearSelectedConcepts: (state, action: PayloadAction) => {
      state.selectedConcepts = [];
    },
  },
  extraReducers: (caseBuilder) => {
    caseBuilder.addCase(searchDocuments.pending, (state, action) => {
      state.fetching = true;
      state.latestSearchRequestId = action.meta.requestId;
    });
    caseBuilder.addCase(searchDocuments.rejected, (state, action) => {
      if (action.meta.requestId !== state.latestSearchRequestId) return;
      state.fetching = false;
    });
    caseBuilder.addCase(searchDocuments.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.latestSearchRequestId) return;
      state.fetching = false;
      state.data = {
        ...action.payload,
        resultaten: [...action.payload.resultaten],
      };
    });
    caseBuilder.addCase(searchBijlages.pending, (state, action) => {
      state.fetchingBijlage = true;
    });
    caseBuilder.addCase(searchBijlages.rejected, (state, action) => {
      state.fetchingBijlage = false;
    });
    caseBuilder.addCase(searchBijlages.fulfilled, (state, action) => {
      state.fetchingBijlage = false;
    });
  },
});

export const { clearSearch, updateSorting, updateSearch, updateFilter, clearSelectedConcepts, updateSelectedConcepts } =
  searchState.actions;
export default searchState.reducer;
